import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MdArrowBack,
  MdArrowForward,
  MdCheckCircle,
  MdEmail,
  MdInsights,
  MdLockOutline,
  MdPersonOutline,
  MdVisibility,
  MdVisibilityOff,
  MdWhatsapp,
} from 'react-icons/md';
import './style.css';
import { useUser } from '../../../contexts/userContext';
import { GlobalContext } from '../../../contexts/GlobalContext';
import api from '../../../services/api';
import { getPreferredHomePath } from '../../../utils/interfaceLayout';

function LoginPage() {
  const { setToken } = useContext(GlobalContext);
  const { loginUser } = useUser();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validatePassword = (value) => {
    if (value.length < 8) {
      setError('A senha deve conter pelo menos 8 caracteres.');
      return false;
    }

    if (!value.match(/[a-z]/g) || !value.match(/[A-Z]/g) || !value.match(/[0-9]/g)) {
      setError('A senha deve conter letras maiúsculas, minúsculas e números.');
      return false;
    }

    return true;
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const loginResponse = await api.post('/users/login', { email, password });
      const token = loginResponse.data.access_token;
      const expiresIn = loginResponse.data.expires_in;

      localStorage.setItem('token', token);
      localStorage.setItem('tokenExpiration', Date.now() + expiresIn * 1000);

      const userInfoResponse = await api.get(`/users/find-by-email?email=${encodeURIComponent(email)}`);
      const userData = userInfoResponse.data;

      localStorage.setItem('user', JSON.stringify(userData));
      setToken(token);
      loginUser(userData);
      navigate(getPreferredHomePath(userData));
    } catch (requestError) {
      setError('Não foi possível entrar. Confira seu e-mail e sua senha.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateUser = async (event) => {
    event.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('As senhas não coincidem.');
      return;
    }

    if (!validatePassword(password)) return;

    setIsSubmitting(true);
    try {
      await api.post('/users/create', { username, email, password });
      setIsCreatingAccount(false);
      setError('');
    } catch (requestError) {
      if (requestError.response?.status === 409) {
        setError('Este e-mail já está em uso.');
      } else {
        setError('Não foi possível criar o usuário.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="syncs-login-page">
      <div className="syncs-login-orb syncs-login-orb-one" />
      <div className="syncs-login-orb syncs-login-orb-two" />

      <button className="syncs-login-back" type="button" onClick={() => navigate('/')}>
        <MdArrowBack aria-hidden="true" /> Voltar ao início
      </button>

      <main className="syncs-login-shell">
        <section className="syncs-login-intro" aria-label="Apresentação do SyncsCRM">
          <div className="syncs-login-brand">
            <img src="/logo192.png" alt="Logotipo SyncsCRM" />
            <span>Syncs<span>CRM</span></span>
          </div>
          <span className="syncs-login-kicker">Sua operação conectada</span>
          <h1>Bem-vindo de volta.</h1>
          <p>
            Continue de onde parou e acompanhe vendas, clientes, atendimento e produção em um único ambiente.
          </p>

          <div className="syncs-login-benefits">
            <article>
              <span><MdInsights /></span>
              <div><strong>Visão clara do negócio</strong><small>Prioridades, etapas e resultados sempre à vista.</small></div>
            </article>
            <article>
              <span><MdWhatsapp /></span>
              <div><strong>Atendimento integrado</strong><small>Conversa e histórico comercial trabalhando juntos.</small></div>
            </article>
            <article>
              <span><MdCheckCircle /></span>
              <div><strong>Rotina organizada</strong><small>Informações centralizadas para toda a equipe.</small></div>
            </article>
          </div>
        </section>

        <section className="syncs-login-card" aria-labelledby="syncs-login-title">
          <div className="syncs-login-card-brand">
            <img src="/logo192.png" alt="" />
            <span>SyncsCRM</span>
          </div>

          {isCreatingAccount ? (
            <>
              <div className="syncs-login-card-heading">
                <span>Cadastro interno</span>
                <h2 id="syncs-login-title">Criar usuário</h2>
                <p>Preencha os dados para criar uma nova conta.</p>
              </div>
              <form className="syncs-login-form" onSubmit={handleCreateUser}>
                <label htmlFor="syncs-login-name">Nome</label>
                <div className="syncs-login-field">
                  <MdPersonOutline aria-hidden="true" />
                  <input id="syncs-login-name" type="text" autoComplete="name" placeholder="Seu nome" value={username} onChange={(event) => setUsername(event.target.value)} required />
                </div>

                <label htmlFor="syncs-create-email">E-mail</label>
                <div className="syncs-login-field">
                  <MdEmail aria-hidden="true" />
                  <input id="syncs-create-email" type="email" autoComplete="email" placeholder="seuemail@empresa.com.br" value={email} onChange={(event) => setEmail(event.target.value)} required />
                </div>

                <label htmlFor="syncs-create-password">Senha</label>
                <div className="syncs-login-field">
                  <MdLockOutline aria-hidden="true" />
                  <input id="syncs-create-password" type={showPassword ? 'text' : 'password'} autoComplete="new-password" placeholder="Crie uma senha segura" value={password} onChange={(event) => setPassword(event.target.value)} required />
                  <button type="button" onClick={() => setShowPassword((visible) => !visible)} aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}>
                    {showPassword ? <MdVisibilityOff /> : <MdVisibility />}
                  </button>
                </div>

                <label htmlFor="syncs-confirm-password">Confirmar senha</label>
                <div className="syncs-login-field">
                  <MdLockOutline aria-hidden="true" />
                  <input id="syncs-confirm-password" type={showPassword ? 'text' : 'password'} autoComplete="new-password" placeholder="Digite a senha novamente" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} required />
                </div>

                <div className="syncs-login-error" role="alert">{error}</div>
                <button className="syncs-login-submit" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Criando conta…' : 'Criar usuário'} <MdArrowForward />
                </button>
              </form>
              <button className="syncs-login-switch" type="button" onClick={() => { setIsCreatingAccount(false); setError(''); }}>
                Já tenho uma conta
              </button>
            </>
          ) : (
            <>
              <div className="syncs-login-card-heading">
                <span>Acesso seguro</span>
                <h2 id="syncs-login-title">Entre na sua conta</h2>
                <p>Use os dados fornecidos pela sua empresa.</p>
              </div>
              <form className="syncs-login-form" onSubmit={handleLogin}>
                <label htmlFor="syncs-login-email">E-mail</label>
                <div className="syncs-login-field">
                  <MdEmail aria-hidden="true" />
                  <input
                    id="syncs-login-email"
                    type="email"
                    autoComplete="username"
                    placeholder="seuemail@empresa.com.br"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    required
                  />
                </div>

                <label htmlFor="syncs-login-password">Senha</label>
                <div className="syncs-login-field">
                  <MdLockOutline aria-hidden="true" />
                  <input
                    id="syncs-login-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="Digite sua senha"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                  />
                  <button type="button" onClick={() => setShowPassword((visible) => !visible)} aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}>
                    {showPassword ? <MdVisibilityOff /> : <MdVisibility />}
                  </button>
                </div>

                <div className="syncs-login-error" role="alert">{error}</div>
                <button className="syncs-login-submit" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Entrando…' : 'Entrar na plataforma'} <MdArrowForward />
                </button>
              </form>
            </>
          )}

          <div className="syncs-login-security"><MdLockOutline /> Acesso restrito a usuários autorizados</div>
          <div className="syncs-login-legal">
            <a href="/politica-de-privacidade.html">Privacidade</a>
            <span>•</span>
            <a href="/exclusao-de-dados.html">Exclusão de dados</a>
          </div>
        </section>
      </main>

      <footer className="syncs-login-footer">© 2022 SyncsCRM</footer>
    </div>
  );
}

export default LoginPage;
